/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { createSolid, createSolidFromString } from '@web-stories-wp/patterns';

/**
 * Internal dependencies
 */
import { MULTIPLE_VALUE } from '../../../../constants';
import { getEffectiveLottieParameterValues } from '../../../../elements/lottie/contract';

const clampColorComponent = (value) =>
  Math.max(0, Math.min(255, Math.round(value)));

const componentToHex = (value) =>
  clampColorComponent(value).toString(16).padStart(2, '0');

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object ?? {}, key);

const findParameter = (element, key) =>
  element.parameterSchema?.parameters?.find(
    (parameter) => parameter.key === key
  );

const getMinValue = (values) =>
  values.length ? Math.min(...values) : undefined;

const getMaxValue = (values) =>
  values.length ? Math.max(...values) : undefined;

export const getParameterLabel = (parameter) =>
  parameter.label || parameter.key;

export const hasParameterOverride = (element, key) =>
  hasOwn(element.parameterValues, key);

export const getEffectiveParameterValue = (element, key) =>
  getEffectiveLottieParameterValues(element)[key];

export const getCommonLottieParameters = (selectedElements) => {
  if (!selectedElements.length) {
    return [];
  }

  const firstParameters = selectedElements[0].parameterSchema?.parameters ?? [];

  return firstParameters.reduce((commonParameters, parameter) => {
    const matchingParameters = selectedElements.map((element) =>
      findParameter(element, parameter.key)
    );

    if (
      matchingParameters.some((matchingParameter) => !matchingParameter) ||
      matchingParameters.some(
        (matchingParameter) => matchingParameter.type !== parameter.type
      )
    ) {
      return commonParameters;
    }

    const maxLengthValues = matchingParameters
      .map((matchingParameter) => matchingParameter.constraints?.maxLength)
      .filter((value) => value !== undefined);
    const minValues = matchingParameters
      .map((matchingParameter) => matchingParameter.constraints?.min)
      .filter((value) => value !== undefined);
    const maxValues = matchingParameters
      .map((matchingParameter) => matchingParameter.constraints?.max)
      .filter((value) => value !== undefined);
    const constraints = {
      ...(maxLengthValues.length
        ? { maxLength: getMinValue(maxLengthValues) }
        : {}),
      ...(minValues.length ? { min: getMaxValue(minValues) } : {}),
      ...(maxValues.length ? { max: getMinValue(maxValues) } : {}),
    };

    return [
      ...commonParameters,
      {
        ...parameter,
        ...(Object.keys(constraints).length ? { constraints } : {}),
      },
    ];
  }, []);
};

export const lottieColorValueToPattern = (value) => {
  if (value === MULTIPLE_VALUE) {
    return MULTIPLE_VALUE;
  }

  if (!value) {
    return createSolid(0, 0, 0);
  }

  if (typeof value === 'string') {
    try {
      return createSolidFromString(value);
    } catch {
      return createSolid(0, 0, 0);
    }
  }

  if (!Array.isArray(value)) {
    return createSolid(0, 0, 0);
  }

  const scale = value.slice(0, 3).every((component) => component <= 1)
    ? 255
    : 1;

  return createSolid(
    clampColorComponent(value[0] * scale),
    clampColorComponent(value[1] * scale),
    clampColorComponent(value[2] * scale)
  );
};

export const patternToHexColor = (pattern) => {
  if (!pattern?.color) {
    return null;
  }

  const { r, g, b } = pattern.color;
  if (![r, g, b].every(Number.isFinite)) {
    return null;
  }

  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(
    b
  )}`.toLowerCase();
};
